import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

export type SuperadminUser = {
  id: string;
  primaryEmail: string;
  displayName: string | null;
  isActive: boolean;
  isSuperadmin: boolean;
  authProvider: string;
  createdAt: string;
};

type UsersResponse = {
  ok: boolean;
  data: SuperadminUser[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export async function fetchSuperadminUsers(
  params: { search?: string; page?: number },
  fetchImpl: FetchLike = fetch
): Promise<UsersResponse> {
  const { search, page = 1 } = params;
  const url = new URL(`${API_URL}/superadmin/users`);
  if (search) url.searchParams.set('search', search);
  url.searchParams.set('page', String(page));

  const res = await fetchImpl(url.toString(), { credentials: 'include' });
  if (!res.ok) {
    throw await toApiError(res, 'Failed to fetch users');
  }
  return (await res.json()) as UsersResponse;
}

export function useSuperadminUsersQuery(params: { search?: string; page?: number }) {
  return useQuery<UsersResponse>({
    queryKey: ['superadmin', 'users', { search: params.search, page: params.page ?? 1 }],
    queryFn: () => fetchSuperadminUsers(params),
  });
}

export function useDeactivateUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`${API_URL}/superadmin/users/${userId}/deactivate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error ?? 'Failed to deactivate user');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
    },
  });
}

export function useReactivateUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`${API_URL}/superadmin/users/${userId}/reactivate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error ?? 'Failed to reactivate user');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
    },
  });
}
